import {
  TableCellProps,
  WbButton,
  WbCardContent,
  WbEntityRefLink,
  WbTable,
  WbWidget,
  customAlertApiRef,
  WbTooltip,
  WbInfoTooltip,
} from '@agilelab/plugin-wb-platform';
import { Chip, Divider, Theme, Typography, useTheme } from '@material-ui/core';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import CachedIcon from '@material-ui/icons/Cached';
import CheckCircle from '@material-ui/icons/CheckCircle';
import HelpIcon from '@material-ui/icons/Help';
import RadioButtonUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import { Alert } from '@material-ui/lab';
import { useEffect } from 'react';
import { useMigrationContext } from '../../hooks/migration/useMigrationContext';
import { TemplateEntityUpdate } from '../../types';

const isUpdated = (e: TemplateEntityUpdate) => e.generates !== undefined;

const columns = (
  theme: Theme,
  onUpdateSelected: (e: TemplateEntityUpdate) => void,
) =>
  [
    {
      headerName: 'Status',
      field: 'status',
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      resizable: false,
      fieldRender: e =>
        e.generates ? (
          <WbTooltip title="Updated">
            <Chip
              clickable
              label="Updated"
              variant="outlined"
              icon={
                <CheckCircle style={{ color: theme.palette.success.main }} />
              }
            />
          </WbTooltip>
        ) : (
          <WbInfoTooltip
            title="Not yet updated"
            icon={<RadioButtonUnchecked color="disabled" />}
          />
        ),
    },
    {
      headerName: 'Name',
      field: 'name',
      fieldRender: e => (
        <WbEntityRefLink entityRef={stringifyEntityRef(e.originalEntity)}>
          <Typography
            style={isUpdated(e) ? {} : { color: theme.palette.text.disabled }}
          >
            {e.name}
          </Typography>
        </WbEntityRefLink>
      ),
    },
    {
      headerName: 'Resource type id',
      field: 'resourceType',
      headerDescription: 'spec.type field in template.yaml',
      fieldRender: e => (
        <Typography
          style={isUpdated(e) ? {} : { color: theme.palette.text.disabled }}
        >
          {e.resourceType}
        </Typography>
      ),
    },
    {
      headerName: 'Generates',
      field: 'generates',
      fieldRender: e => (
        <Typography
          style={isUpdated(e) ? {} : { color: theme.palette.text.disabled }}
        >
          {e.generates ? (
            <WbEntityRefLink title={e.generates.name} entityRef={e.generates} />
          ) : (
            '--'
          )}
        </Typography>
      ),
    },
    {
      field: 'update',
      cellProps: {
        align: 'center',
      },
      fieldRender: e => {
        if (isUpdated(e)) return <></>;
        if (e.updateAvailable) {
          return (
            <WbButton
              label="View update"
              color="primary"
              size="small"
              variant="outlined"
              loading={false}
              disabled={false}
              onClick={ev => {
                ev.preventDefault();
                ev.stopPropagation();
                onUpdateSelected(e);
              }}
            />
          );
        }
        return (
          <WbTooltip title="A SystemType or ComponentType referencing the resource type id handled by this template is not yet registered. Make sure to fulfill all the previous migration steps">
            <Chip
              size="small"
              variant="outlined"
              icon={<HelpIcon />}
              label="Update not yet available"
            />
          </WbTooltip>
        );
      },
    },
  ] as TableCellProps<TemplateEntityUpdate>[];

const sortPosition = (e: TemplateEntityUpdate) => {
  if (isUpdated(e)) return 2;
  if (!e.updateAvailable) return 1;
  return 0;
};

export const TemplateUpdatesWidget = (props: {
  onViewUpdate?: (entity: TemplateEntityUpdate) => void;
}) => {
  const theme = useTheme();
  const { fetchTemplateUpdatesState, fetchTemplateUpdates } =
    useMigrationContext();
  const alertApi = useApi(customAlertApiRef);

  useEffect(() => {
    if (fetchTemplateUpdatesState.error) {
      alertApi.post({
        message: `Error while loading templates: ${fetchTemplateUpdatesState.error.message}`,
        severity: 'error',
      });
    }
  }, [fetchTemplateUpdatesState, alertApi]);

  return (
    <WbWidget
      title="Templates to update"
      actions={
        <WbTooltip title="Refresh">
          <WbButton
            variant="outlined"
            onClick={() => fetchTemplateUpdates()}
            label="Refresh"
            icon={<CachedIcon />}
            disabled={fetchTemplateUpdatesState.loading}
          />
        </WbTooltip>
      }
    >
      <WbCardContent>
        <Alert
          severity="info"
          variant="outlined"
          icon={<HelpIcon />}
          style={{ border: 'none' }}
        >
          <Typography>
            Update the <code>template.yaml</code> file of the following
            templates by adding the <code>generates</code> property under the{' '}
            <code>spec</code> object. This field serves as a reference to the{' '}
            <code>SystemType</code> or <code>ComponentType</code> of the
            entities generated by the template. Click on the "View Update"
            button to correctly update the registered templates. It will be
            visible once the previous migration steps are completed
          </Typography>
        </Alert>
        <Divider />
        <WbTable<TemplateEntityUpdate>
          components={{
            tableLoader: {
              loading: fetchTemplateUpdatesState.loading,
            },
            tableContent: {
              columns: columns(theme, e => props.onViewUpdate?.(e)),
              rows:
                fetchTemplateUpdatesState.value?.sort(
                  (a, b) => sortPosition(a) - sortPosition(b),
                ) ?? [],
            },
          }}
        />
      </WbCardContent>
    </WbWidget>
  );
};
